import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { toast } from "sonner";
import { Mail, Check, ArrowLeft } from "lucide-react";
import { Spinner } from "../components/Spinner";
import { useAuth } from "../contexts/AuthContext";

export function Verification() {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail, resendVerificationEmail } = useAuth();

  // Get email from route state or localStorage
  const email =
    location.state?.email ||
    localStorage.getItem("pendingVerificationEmail") ||
    "user@example.com";

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!code || code.trim().length === 0) {
      toast.error("Please enter the verification code");
      return;
    }

    setIsVerifying(true);

    try {
      const result = await verifyEmail({ token: code.trim() });

      if (result.success) {
        setIsVerified(true);
        toast.success("Email verified successfully!");
        localStorage.removeItem("pendingVerificationEmail");

        // Redirect after success
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else if (result.general) {
        setError(result.general);
        toast.error(result.general);
      } else {
        setError("Verification failed. Please try again.");
        toast.error("Verification failed. Please try again.");
      }
    } catch (error) {
      setError("An error occurred during verification. Please try again.");
      toast.error("An error occurred during verification");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setIsResending(true);

    try {
      const result = await resendVerificationEmail(email);

      if (result.success) {
        setCanResend(false);
        setCountdown(60);
        toast.success("Verification code sent!");
      } else if (result.general) {
        toast.error(result.general);
      } else {
        toast.error("Failed to resend code. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => navigate("/auth")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to sign in</span>
        </button>

        {/* Main Card */}
        <div className="bg-card border border-border rounded-xl shadow-sm p-8">
          {/* Icon */}
          <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
            {isVerified ? (
              <Check className="w-8 h-8 text-primary" />
            ) : (
              <Mail className="w-8 h-8 text-muted-foreground" />
            )}
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="mb-2">
              {isVerified ? "Email Verified!" : "Verify Your Email"}
            </h1>
            <p className="text-muted-foreground">
              {isVerified
                ? "Your email has been successfully verified"
                : `We've sent a 6-digit code to`}
            </p>
            {!isVerified && <p className="text-foreground mt-1">{email}</p>}
          </div>

          {!isVerified ? (
            <>
              {/* Verification Code Input */}
              <form onSubmit={handleVerify} className="space-y-6">
                <div>
                  <label className="block text-center mb-4">
                    Enter verification code
                  </label>
                  <div className="flex justify-center">
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Paste your verification code here"
                      className="w-full px-4 py-3 text-center bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                      disabled={isVerifying}
                    />
                  </div>
                </div>

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isVerifying || !code.trim()}
                  className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <Spinner size="sm" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    "Verify Email"
                  )}
                </button>
                <div className="mt-4 text-center">
                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
              </form>

              {/* Resend Code */}
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Didn't receive the code?
                </p>
                <button
                  onClick={handleResend}
                  disabled={!canResend || isResending}
                  className="text-sm text-foreground hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {isResending ? (
                    <>
                      <Spinner size="sm" />
                      <span>Sending...</span>
                    </>
                  ) : canResend ? (
                    "Resend code"
                  ) : (
                    `Resend code in ${countdown}s`
                  )}
                </button>
              </div>
            </>
          ) : (
            // Success State
            <div className="text-center">
              <p className="text-muted-foreground mb-4">
                Redirecting to dashboard...
              </p>
              <div className="flex justify-center">
                <Spinner size="md" />
              </div>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Please check your spam folder if you don't see the email in your
            inbox.
          </p>
        </div>
      </div>
    </div>
  );
}
